// ADR-198: Reports → Scheduled Jobs (read-only, phase 1).
//
// Live view of radar-scheduler: what runs when, what is running now, and how
// the latest run of each job ended. Outcomes are held in the scheduler's
// memory and reset when it restarts; persisted history per job is phase 2.
// Jobs that already keep run history link to their report.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSyncAlt } from 'react-icons/fa';
import { Alert } from '@mui/material';
import {
  Container, Header, HeaderLeft, Title, Content, MainSection, BackButton,
} from '../styles';
import { HeaderButton } from '../../common/styles';
import { useAbility } from '../../../contexts/AbilityContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useScheduledJobs } from '../../../hooks/useScheduledJobs';
import { summarizeJobs, nextFire, formatRelative } from '../../../utils/scheduledJobs';
import {
  SummaryGrid, SummaryTile, SummaryLabel, SummaryValue, SummaryMeta,
} from '../DirectManagerDiscrepancies/styles';
import JobsTable from './JobsTable';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const UNAVAILABLE_CODES = ['SCHEDULER_UNAVAILABLE', 'SCHEDULER_NOT_CONFIGURED'];

const ScheduledJobs = () => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { data, isLoading, isRefreshing, error, loadedAt, refetch } = useScheduledJobs();

  const schedulers = data?.schedulers || [];
  const summary = summarizeJobs(schedulers);
  const soonest = nextFire(schedulers);
  const timezones = [...new Set(schedulers.filter((s) => s.schedule?.type !== 'manual-only').map((s) => s.timezone).filter(Boolean))];
  const now = loadedAt || new Date();
  const unavailable = error && UNAVAILABLE_CODES.includes(error?.data?.code);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Scheduled Jobs</Title>
        </HeaderLeft>
        <HeaderButton onClick={refetch} disabled={isLoading || isRefreshing} title="Refreshes every minute while this page is open">
          <FaSyncAlt />
          {isRefreshing ? 'Refreshing…' : 'Refresh'}
        </HeaderButton>
      </Header>

      <Content>
        <MainSection>
          {isLoading && <LoadingSpinner />}

          {!isLoading && error && !data && (
            <EmptyState
              title={unavailable ? 'The scheduler is unavailable' : 'Failed to load scheduled jobs'}
              description={unavailable
                ? 'Radar could not reach the scheduler service. Jobs may still be running; try again shortly.'
                : (error?.message || 'Try refreshing the page.')}
            />
          )}

          {!isLoading && data && (
            <>
              {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {`The last refresh failed; showing data from ${formatDateTime(loadedAt)}.`}
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 2 }}>
                {`Live status from the scheduler, running since ${formatDateTime(data.service?.startedAt)}. `}
                {'Last-run outcomes reset when the scheduler restarts; jobs that keep their own run history link to it. '}
                {timezones.length > 0 && `Schedules are in the scheduler's timezone (${timezones.join(', ')}); dates are shown in your local time.`}
              </Alert>

              <SummaryGrid>
                <SummaryTile>
                  <SummaryLabel>Jobs</SummaryLabel>
                  <SummaryValue>{summary.total}</SummaryValue>
                  <SummaryMeta>{`${summary.neverRan} not run since restart`}</SummaryMeta>
                </SummaryTile>
                <SummaryTile>
                  <SummaryLabel>Running now</SummaryLabel>
                  <SummaryValue>{summary.running}</SummaryValue>
                </SummaryTile>
                <SummaryTile>
                  <SummaryLabel>Last run failed</SummaryLabel>
                  <SummaryValue style={summary.failing > 0 ? { color: '#991B1B' } : undefined}>{summary.failing}</SummaryValue>
                  <SummaryMeta>{summary.skipped > 0 ? `${summary.skipped} with skipped fires` : 'No skipped fires'}</SummaryMeta>
                </SummaryTile>
                <SummaryTile>
                  <SummaryLabel>Next up</SummaryLabel>
                  <SummaryValue>{soonest ? formatRelative(soonest.nextRun, now) : '—'}</SummaryValue>
                  <SummaryMeta>{soonest ? soonest.title : 'Nothing scheduled'}</SummaryMeta>
                </SummaryTile>
              </SummaryGrid>

              {schedulers.length === 0 ? (
                <EmptyState title="No jobs registered" description="The scheduler is running but reports no jobs." />
              ) : (
                <JobsTable schedulers={schedulers} categories={data.categories} ability={ability} now={now} />
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default ScheduledJobs;
